import { useQueryClient } from '@tanstack/react-query';
import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import RootLoader from '../components/root-loader/index';
import { BackendRoute } from '../config/index';
import { QueryKey } from '../constants/index';
import { HttpException } from '../exceptions/index';
import { Company, useAppMutation } from '../hooks/index';
import { useClaimSubscriptionOffer } from '../hooks/subscription-offer/use-claim-subscription-offer';
import { ApiService } from '../services/index';
import { inviteStakeholderStore } from '../storage/local/index';
import { showOnboardingPageState, subscriptionOfferStore } from '../storage/local/index';
import { buildLoginPathWithRedirect, persistPostAuthRedirect } from '../utils/auth-redirect.util';
import { resolveUnfinishedCompanySessionRedirect } from '../utils/unfinished-company-creation-redirect.util';
import { useStakeholderPermissionGuard } from './use-stakeholder-permission-guard';

/** Failsafe when validate/invitation bootstrap never settles (e.g. hung /api proxy). */
const AUTH_BOOTSTRAP_TIMEOUT_MS = 45_000;

interface AuthGuardProps {
  children: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
  const { claimSubscriptionOffer } = useClaimSubscriptionOffer();
  const navigate = useNavigate();
  const location = useLocation();
  const loginPath = buildLoginPathWithRedirect(`${location.pathname}${location.search}`);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInvitationHandled, setIsInvitationHandled] = useState(false);
  const [isOfferHandled, setIsOfferHandled] = useState(false);
  const [isUnfinishedCompanyHandled, setIsUnfinishedCompanyHandled] = useState(false);
  const [isBootstrapTimedOut, setIsBootstrapTimedOut] = useState(false);

  const {
    mutate,
    isPending: isLoading,
    isError: isValidateError,
  } = useAppMutation([BackendRoute.AUTH_VALIDATE], {
    mutationFn: () => ApiService.get({ endpoint: BackendRoute.AUTH_VALIDATE }),
    retry: false,
    onSuccess: () => setIsAuthenticated(true),
    onError: () => {
      void ApiService.clearStaleAuthSession();
    },
    defaultErrorHandling: false,
  });

  const unauthorizedRedirect = useStakeholderPermissionGuard();
  const queryClient = useQueryClient();

  Company.useCheckModuleAccess(isAuthenticated);
  const { isSelectingCompany } = Company.useSelectCompanyFromQueryParam(isAuthenticated);

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_SELECTED_COMPANY],
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_COMPANIES],
    });
  }, [queryClient]);

  const handleInvitation = useCallback(async () => {
    const invitation = inviteStakeholderStore.get();
    if (!invitation?.id || invitation?.isAccepted) {
      setIsInvitationHandled(true);
      return;
    }

    try {
      await ApiService.get({
        endpoint: BackendRoute.INVITATIONS,
        routePath: ['accept', invitation.id],
      });
      inviteStakeholderStore.set({
        ...invitation,
        isAccepted: true,
      });

      invalidateQuery();
    } catch (e) {
      if (e instanceof HttpException) {
        toast.error(e.body?.message);
      }
      inviteStakeholderStore.delete();

      navigate('/error-page');
    } finally {
      setIsInvitationHandled(true);
    }
  }, [invalidateQuery, navigate]);

  const handleSubscriptionOffer = useCallback(async () => {
    const offer = subscriptionOfferStore.get();
    if (!offer?.slug) {
      setIsOfferHandled(true);
      return;
    }

    try {
      const { companyId } = await claimSubscriptionOffer(offer.slug);
      subscriptionOfferStore.delete();
      invalidateQuery();
      navigate(`/billing/${companyId}`, { replace: true });
    } catch (e) {
      // Domain mismatch (403) and already-activated (409) both carry a customer-facing message
      // from the backend; surface it and drop the user on My Companies. Transient failures (no
      // HttpException) keep the stored offer so the claim retries on the next load.
      if (e instanceof HttpException) {
        toast.error(e.body?.message);
        subscriptionOfferStore.delete();
      }
      navigate('/my-companies', { replace: true });
    } finally {
      setIsUnfinishedCompanyHandled(true);
      setIsOfferHandled(true);
    }
  }, [claimSubscriptionOffer, invalidateQuery, navigate]);

  const handleUnfinishedCompany = useCallback(async () => {
    if (showOnboardingPageState.get()) {
      setIsUnfinishedCompanyHandled(true);
      return;
    }

    try {
      const redirectTo = await resolveUnfinishedCompanySessionRedirect(location.pathname);
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      }
    } finally {
      setIsUnfinishedCompanyHandled(true);
    }
  }, [location.pathname, navigate]);

  const hasRequestedValidateRef = useRef(false);

  useEffect(() => {
    if (hasRequestedValidateRef.current) {
      return;
    }
    hasRequestedValidateRef.current = true;
    setIsBootstrapTimedOut(false);
    mutate();
  }, [mutate]);

  const isValidatingSession = isLoading && !isAuthenticated;

  useEffect(() => {
    if (!isLoading && isValidateError) {
      persistPostAuthRedirect(`${location.pathname}${location.search}`);
    }
  }, [isLoading, isValidateError, location.pathname, location.search]);

  useEffect(() => {
    const isStillBootstrapping =
      isValidatingSession ||
      (!isAuthenticated && !isValidateError) ||
      !isInvitationHandled ||
      !isOfferHandled ||
      !isUnfinishedCompanyHandled ||
      isSelectingCompany;

    if (!isStillBootstrapping) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsBootstrapTimedOut(true);
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [
    isAuthenticated,
    isInvitationHandled,
    isOfferHandled,
    isUnfinishedCompanyHandled,
    isSelectingCompany,
    isValidatingSession,
    isValidateError,
  ]);

  useEffect(() => {
    if (!isBootstrapTimedOut) {
      return;
    }
    void ApiService.clearStaleAuthSession();
  }, [isBootstrapTimedOut]);

  useEffect(() => {
    const showOnboardingPage = showOnboardingPageState.get();
    if (showOnboardingPage) {
      navigate('/onboarding');
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    handleInvitation();
  }, [handleInvitation, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    handleSubscriptionOffer();
  }, [handleSubscriptionOffer, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isInvitationHandled || !isOfferHandled || isUnfinishedCompanyHandled) {
      return;
    }

    handleUnfinishedCompany();
  }, [
    handleUnfinishedCompany,
    isAuthenticated,
    isInvitationHandled,
    isOfferHandled,
    isUnfinishedCompanyHandled,
  ]);

  if (isBootstrapTimedOut) {
    return <Navigate replace to={loginPath} />;
  }

  if (!isLoading && isValidateError) {
    return <Navigate replace to={loginPath} />;
  }

  if (
    isValidatingSession ||
    !isAuthenticated ||
    !isInvitationHandled ||
    !isOfferHandled ||
    !isUnfinishedCompanyHandled ||
    isSelectingCompany
  ) {
    return <RootLoader />;
  }

  if (unauthorizedRedirect) {
    return <Navigate replace to={unauthorizedRedirect} />;
  }

  return <>{children}</>;
};

export default AuthGuard;
